import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { ArrowRight, CheckCircle, Play } from "@phosphor-icons/react";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7, ease: "easeOut" },
};

const STEPS = [
    { n: "01", title: "Radar nazionale", text: "Imposti le aree. Il sistema trova e filtra le aste ogni giorno." },
    { n: "02", title: "Analisi AI", text: "Perizia, mercato, rischi e costi analizzati in pochi minuti." },
    { n: "03", title: "Utile netto", text: "Tutti i costi in fila: sai quanto resta davvero in tasca." },
    { n: "04", title: "Decisione", text: "Score, maximum bid e verdetto. Poi decidi tu." },
];

export const LandingHow = forwardRef(function LandingHow({ started, loading, onPlay, videoRef, vttSrc, posterSrc }, _ref) {
    return (
        <section id="come-funziona" className="bg-[#F7F7F5] py-24 sm:py-28 scroll-mt-14" data-testid="landing-how">
            <div className="max-w-[1080px] mx-auto px-6 md:px-10">
                <div className="lp-mono text-[10px] tracking-[0.28em] mb-5 text-emerald-700">COME FUNZIONA</div>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-[#0A0A0A] text-4xl sm:text-5xl leading-[1.02] tracking-tight max-w-[560px]">
                    Quattro passaggi.<br /><span className="text-black/40">Zero ricerche manuali.</span>
                </motion.h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
                    {STEPS.map((s, i) => (
                        <motion.div key={s.n} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1, duration: 0.5 }}
                                    data-testid={`how-step-${i}`}>
                            <div className="lp-mono text-[11px] tracking-[0.2em] text-emerald-700 mb-2.5">{s.n}</div>
                            <h3 className="lp-display font-bold text-lg text-[#0A0A0A]">{s.title}</h3>
                            <p className="lp-body text-[13px] text-black/55 leading-relaxed mt-1.5">{s.text}</p>
                        </motion.div>
                    ))}
                </div>
                <motion.div {...fadeUp} id="demo" className="mt-14 scroll-mt-24">
                    <div className="relative bg-[#000000] rounded-2xl overflow-hidden border border-black/10 shadow-[0_24px_70px_rgba(0,0,0,0.14)]" data-testid="intro-video-card">
                        <video ref={videoRef} data-testid="intro-demo-video" className="w-full aspect-video block"
                               controls poster={posterSrc} preload="none" playsInline>
                            {vttSrc && <track kind="subtitles" srcLang="it" label="Italiano" src={vttSrc} default />}
                        </video>
                        {!started && (
                            <button onClick={onPlay} data-testid="intro-play-overlay"
                                    className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-[2px] hover:bg-black/30 transition-colors group">
                                <span className="w-[72px] h-[72px] rounded-full bg-[#FFFFFF] text-[#0A0A0A] flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform">
                                    {loading
                                        ? <span className="w-7 h-7 rounded-full border-[3px] border-black/20 border-t-black animate-spin" />
                                        : <Play size={30} weight="fill" className="ml-1" />}
                                </span>
                                <span className="lp-mono text-white text-[11px] tracking-[0.24em]">
                                    {loading ? "CARICAMENTO VIDEO…" : "TOUR REALE · 60S · CON AUDIO"}
                                </span>
                            </button>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
});

const PLANS = [
    {
        name: "Standard", price: "€19,90", period: "/mese", limit: "5 analisi al mese", featured: true, testid: "pricing-standard-btn",
        cta: "Scegli Standard", plan: "standard",
        features: ["Radar aste nazionale", "5 analisi che si rinnovano ogni mese", "Report con offerta massima consigliata", "OCR perizie & import automatico", "Simulatore di rilancio"],
    },
    {
        name: "Pro", price: "€49,90", period: "/mese", limit: "25 analisi al mese", testid: "pricing-pro-btn",
        cta: "Scegli Pro", plan: "pro",
        features: ["Tutto del piano Standard", "25 analisi che si rinnovano ogni mese", "Confronto opportunità & export", "Supporto prioritario dedicato"],
    },
];

export const LandingPricing = () => {
    const { user } = useAuth();
    return (
        <section id="prezzi" className="bg-[#F7F7F5] border-t border-black/[0.07] py-24 sm:py-28 scroll-mt-14" data-testid="landing-pricing">
            <div className="max-w-[1080px] mx-auto px-6 md:px-10">
                <div className="lp-mono text-[10px] tracking-[0.28em] mb-5 text-emerald-700">PREZZI</div>
                <motion.h2 {...fadeUp} className="lp-display font-extrabold text-[#0A0A0A] text-4xl sm:text-5xl leading-[1.02] tracking-tight max-w-[560px]">
                    Scegli il tuo piano.<br /><span className="text-black/40">Disdici quando vuoi.</span>
                </motion.h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start mt-12 max-w-[760px] mx-auto">
                    {PLANS.map((p, i) => (
                        <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1, duration: 0.55 }}
                                    className={`relative p-7 rounded-2xl bg-[#FFFFFF] flex flex-col h-full border ${p.featured ? "border-emerald-600 shadow-[0_20px_60px_rgba(0,0,0,0.09)]" : "border-black/[0.09]"}`}>
                            {p.featured && (
                                <span className="lp-mono absolute -top-3 left-7 bg-emerald-600 text-white text-[9px] tracking-[0.18em] px-3 py-1 rounded-full font-bold">PIÙ SCELTO</span>
                            )}
                            <h3 className="lp-display font-bold text-xl text-[#0A0A0A]">{p.name}</h3>
                            <div className="mt-2 flex items-end gap-1">
                                <span className="lp-mono font-bold text-[34px] tracking-tight text-[#0A0A0A]">{p.price}</span>
                                {p.period && <span className="lp-body text-xs text-black/40 mb-1.5">{p.period}</span>}
                            </div>
                            <div className="lp-body mt-1 text-[13px] font-semibold text-emerald-700">{p.limit}</div>
                            <ul className="flex flex-col gap-2.5 mt-6 mb-7 flex-grow">
                                {p.features.map((f) => (
                                    <li key={f} className="lp-body flex items-start gap-2.5 text-[13px] text-black/60">
                                        <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> {f}
                                    </li>
                                ))}
                            </ul>
                            <Link to={user ? "/piano" : `/register?plan=${p.plan}`} data-testid={p.testid}
                                  className={`h-11 inline-flex items-center justify-center gap-2 text-sm font-bold rounded-full transition-colors active:scale-[0.97] ${p.featured ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-[#0A0A0A] text-white hover:bg-black/80"}`}>
                                {p.cta} <ArrowRight size={14} weight="bold" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
                <p className="lp-mono text-[9px] tracking-[0.14em] text-black/35 mt-7">PREZZI IVA ESCLUSA · DISDICI O CAMBIA PIANO QUANDO VUOI · PAGAMENTO SICURO STRIPE</p>
            </div>
        </section>
    );
};
